import { createApi } from '@reduxjs/toolkit/query/react';
import { axiosBaseQuery } from "@src/service";
import { OrderEntity, OrderMissionEntity as OrderMissionEntityV2 } from '@src/core/features';
import { OrderGroupEntity} from '@src/features/order/entities';
import { IGetOrdersRequest, IGetOrdersByStatusResponse, IRequestAuthResponse, ISendCommandRequest, ITakeOffRequest } from "./types";

export const orderFeatureApi = createApi({
  baseQuery: axiosBaseQuery(),
  reducerPath: 'orderFeatureApi',
  tagTypes: ['Orders', 'OrderDetails'],
  endpoints: (builder) => ({

    getOrders: builder.query<OrderGroupEntity, void>({
      query: (params) => ({
        url: `/api/v1/orders?page=1&size=100`,
        method: 'GET',
        params,
      }),
      providesTags: ['Orders'],
    }),

    getOrder: builder.query<OrderEntity, string | number | undefined>({
      query: (orderId) => ({
        url: `/api/v1/orders/${orderId}`,
        method: 'GET',
      }),
      keepUnusedDataFor: 0,
    }),

    getOrderMission: builder.query<OrderMissionEntityV2, string | undefined>({
      providesTags: ['OrderDetails'],
      query: (missionId) => ({
        url: `/api/v1/orders/mission/${missionId}`,
        method: 'GET',
      }),
      keepUnusedDataFor: 0,
      transformResponse: (response: OrderMissionEntityV2) => {
        const [ order ] = response.orders;
        return {
          ...response,
          status: order.status,
        };
      }
    }),

    getOrdersByStatus: builder.query<IGetOrdersByStatusResponse, IGetOrdersRequest>({
      query: ({ status, page, size, search }) => ({
        url: '/api/v1/orders/by-status',
        method: 'GET',
        params: {
          status,
          page,
          size,
          ...(search?.trim() ? { search: search.trim() } : {}),
        },
      }),
    }),

    cancelOrder: builder.mutation<void, number | string | undefined>({
      invalidatesTags: ['Orders'],
      query: (orderId) => ({
        url: `/api/v1/orders/${orderId}/cancel`,
        method: 'PUT'
      })
    }),

    cancelMission: builder.mutation<void, string | undefined>({
      invalidatesTags: ['Orders'],
      query: (missionId) => ({
        url: `/api/v1/orders/mission/${missionId}/cancel`,
        method: 'PUT',
      })
    }),

    requestAuth: builder.mutation<IRequestAuthResponse, string>({
      invalidatesTags: ['Orders'],
      query: (missionId) => ({
        url: `/api/v1/orders/${missionId}/authorization`,
        method: 'GET',
      })
    }),

    takeOff: builder.mutation<void, ITakeOffRequest>({
      invalidatesTags: ['Orders', 'OrderDetails'],
      query: ({ missionId }) => ({
        // Mission takeoff carries no dimensional data (altitude comes from the stored
        // mission); the backend endpoint takes no body, so nothing to send/guard.
        url: `/api/v1/orders/${missionId}/TAKE_OFF`,
        method: 'POST',
      })
    }),

    sendCommand: builder.mutation<void, ISendCommandRequest>({
      query: ({ missionId, command }) => ({
        url: `/api/v1/orders/${missionId}/${command}`,
        method: 'POST',
      })
    }),
  })
});

export const { 
  useGetOrdersQuery, 
  useSendCommandMutation,
  useGetOrdersByStatusQuery,
  useGetOrderMissionQuery,
  useGetOrderQuery,
  useTakeOffMutation,
  useCancelOrderMutation,
  useRequestAuthMutation,
  useCancelMissionMutation,
} = orderFeatureApi;